// ExtracurricularStep.js - Extracurricular activity input step
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from '../../i18n/useTranslation';

const SUGGESTIONS = [
  { id: 'robotics', label: 'Robotics Club', icon: 'robot' },
  { id: 'debate', label: 'Debate Team', icon: 'account-voice' },
  { id: 'sports', label: 'Sports Team', icon: 'basketball' },
  { id: 'music', label: 'Music/Band', icon: 'music' },
  { id: 'art', label: 'Art Club', icon: 'palette' },
  { id: 'science', label: 'Science Club', icon: 'flask' },
  { id: 'drama', label: 'Drama/Theater', icon: 'drama-masks' },
  { id: 'volunteer', label: 'Volunteering', icon: 'hand-heart' },
];

const ExtracurricularStep = ({ value = '', onChange, onNext, onBack }) => {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const [activity, setActivity] = useState(value);

  useEffect(() => {
    setActivity(value);
  }, [value]);

  const colors = {
    bg: isDark ? '#1e293b' : '#f8fafc',
    card: isDark ? '#334155' : '#ffffff',
    text: isDark ? '#f8fafc' : '#1e293b',
    textSecondary: isDark ? '#94a3b8' : '#64748b',
    accent: '#8b5cf6',
    border: isDark ? '#475569' : '#e2e8f0',
    inputBg: isDark ? '#1e293b' : '#f8fafc',
    chipBg: isDark ? '#475569' : '#e2e8f0',
  };

  const handleChange = (text) => {
    setActivity(text);
    onChange?.(text);
  };

  const selectSuggestion = (suggestion) => {
    const label = t(suggestion.label);
    setActivity(label);
    onChange?.(label);
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.bg }]}>
      <View style={[styles.content, { backgroundColor: colors.card, borderColor: colors.border }]}>
        {/* Icon */}
        <View style={[styles.iconContainer, { backgroundColor: colors.accent + '15' }]}>
          <MaterialCommunityIcons name="run" size={48} color={colors.accent} />
        </View>

        {/* Title */}
        <Text style={[styles.title, { color: colors.text }]}>
          {t('Any extracurricular activities?')}
        </Text>
        <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
          {t('Clubs, teams, or activities outside of class')}
        </Text>

        {/* Input */}
        <TextInput
          style={[
            styles.input,
            {
              backgroundColor: colors.inputBg,
              borderColor: activity ? colors.accent : colors.border,
              color: colors.text,
            },
          ]}
          value={activity}
          onChangeText={handleChange}
          placeholder={t('Type your activity')}
          placeholderTextColor={colors.textSecondary}
        />

        {/* Suggestions */}
        <View style={styles.suggestionsGrid}>
          {SUGGESTIONS.map((suggestion) => {
            const label = t(suggestion.label);
            const isSelected = activity === label;
            return (
              <TouchableOpacity
                key={suggestion.id}
                style={[
                  styles.suggestionChip,
                  {
                    backgroundColor: isSelected ? colors.accent + '20' : colors.chipBg,
                    borderColor: isSelected ? colors.accent : 'transparent',
                  },
                ]}
                onPress={() => selectSuggestion(suggestion)}
              >
                <MaterialCommunityIcons
                  name={suggestion.icon}
                  size={18}
                  color={isSelected ? colors.accent : colors.textSecondary}
                />
                <Text
                  style={[
                    styles.suggestionText,
                    { color: isSelected ? colors.accent : colors.text },
                  ]}
                >
                  {label}
                </Text>
              </TouchableOpacity>
            );
          })}
        </View>

        {/* Navigation Buttons */}
        <View style={styles.buttonRow}>
          <TouchableOpacity
            style={[styles.secondaryButton, { borderColor: colors.border }]}
            onPress={onBack}
            activeOpacity={0.7}
          >
            <MaterialCommunityIcons name="arrow-left" size={18} color={colors.text} />
            <Text style={[styles.secondaryButtonText, { color: colors.text }]}>{t('Back')}</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.primaryButton, { backgroundColor: colors.accent }]}
            onPress={onNext}
            activeOpacity={0.8}
          >
            <Text style={styles.primaryButtonText}>
              {activity ? t('Continue') : t('Skip')}
            </Text>
            <MaterialCommunityIcons name="arrow-right" size={18} color="#fff" />
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  content: {
    width: '100%',
    maxWidth: 520,
    borderRadius: 16,
    padding: 32,
    borderWidth: 1,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 4,
  },
  iconContainer: {
    width: 88,
    height: 88,
    borderRadius: 44,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 24,
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 15,
    textAlign: 'center',
    marginBottom: 24,
  },
  input: {
    width: '100%',
    borderWidth: 2,
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 14,
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 20,
  },
  suggestionsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 8,
    marginBottom: 28,
  },
  suggestionChip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1.5,
    gap: 6,
  },
  suggestionText: {
    fontSize: 13,
    fontWeight: '500',
  },
  buttonRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
    width: '100%',
  },
  secondaryButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 10,
    borderWidth: 1,
    gap: 6,
  },
  secondaryButtonText: {
    fontSize: 15,
    fontWeight: '500',
  },
  primaryButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 10,
    gap: 6,
  },
  primaryButtonText: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '600',
  },
});

export default ExtracurricularStep;
